import { PLAN_ACCESS_OPTIONS } from 'Common/Constants/Constants'
import { PlanTableRow } from 'Features/OperationalPlanner/OperationalPlanner.types'
import {
  SourceSiteDetails,
  WorkcentreDetails,
} from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.types'
import { format, addMinutes } from 'date-fns'
import { id } from 'date-fns/locale'
import { mkConfig, generateCsv, asBlob } from 'export-to-csv'
import { AcceptedData } from 'export-to-csv/output/lib/types'

const csvConfig = mkConfig({ useKeysAsHeaders: true })

export const convertToTitleCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1)
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const replaceUnderScoreWithSpace = (str: string) => {
  return str.replace(/_/gi, ' ')
}

export const formatDateToUTC = (dateString: string) => {
  const date = new Date(dateString)
  return `${format(
    addMinutes(date, date.getTimezoneOffset()),
    'dd/MM/yyyy - HH:mm'
  )} UTC`
}

export const getISOWeek = function (dateString: string) {
  const date = new Date(dateString)
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))
  const week1 = new Date(date.getFullYear(), 0, 4)
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    )
  )
}

export const isValidNumber = (value: string) => {
  const VALID_NUMBER_PATTERN = /^[+]?((\.\d{0,3})|(\d+(\.\d{0,3})?)|(\d+\.))$/
  return value.match(VALID_NUMBER_PATTERN)
}

export const autoHeightInModal = (el: HTMLDivElement, containerId?: string) => {
  document
    .getElementById(containerId ?? '#table-modal-container-mui')
    ?.style.setProperty('height', `${Math.floor(400)}px`)
}
/*
  autoHeight method is used to set the height of the table auto.
  it can be used like ref={autoHeight} or ref={(el) => autoHeight(el, containerId)}
  Use the second option if id of the container is not #table-container-mui.
*/

type AutoHeightArgsType = (el?: HTMLDivElement) => number
export const getTableHeight = (el?: HTMLDivElement) => {
  const windowHeight = window.innerHeight
  const footerHeight = 60
  const adjustment = 40
  let topOftheTable = 0
  if (!el) {
    topOftheTable =
      document.getElementById('table-container-mui')?.getBoundingClientRect()
        .top ?? 280
  } else {
    topOftheTable = el?.getBoundingClientRect().top ?? 280
  }

  const heightOftheTable =
    windowHeight - footerHeight - adjustment - topOftheTable
  return heightOftheTable < 300 ? 300 : heightOftheTable
}
export const autoHeight = (el: HTMLDivElement, containerId?: string) => {
  const heightOftheTable = getTableHeight(el)
  // Set the height of the closest container
  if (el) {
    el.closest('.table-container-mui')?.style.setProperty(
      'height',
      `${Math.floor(heightOftheTable < 300 ? 300 : heightOftheTable)}px`
    )
    return
  }
  if (containerId) {
    document
      .getElementById(containerId)
      ?.style.setProperty(
        'height',
        `${Math.floor(heightOftheTable < 300 ? 300 : heightOftheTable)}px`
      )
  }
}

export const setTableContainerHeight = (el: HTMLDivElement, height: number) => {
  if (!el) return
  el.closest('.table-container-mui')?.style.setProperty('height', `${height}px`)
}

// When we have week in the format 'Week 1' and 'Week 2'
export const weekSorter = (a: string, b: string) => {
  const first = a.match(/\d+/)
  const second = b.match(/\d+/)

  if (first && second) {
    return parseInt(first[0]) - parseInt(second[0])
  }
  return 0
}

export const skuCodeSort = (a: string, b: string) => {
  return ('' + a).localeCompare(b)
}

export const sourceSitesLabelSort = (
  a: SourceSiteDetails,
  b: SourceSiteDetails
) => {
  return ('' + a.siteLabel.toLocaleLowerCase()).localeCompare(
    b.siteLabel.toLocaleLowerCase()
  )
}
export const sourceSitesCodeSort = (
  a: WorkcentreDetails,
  b: WorkcentreDetails
) => {
  return ('' + a.code.toLocaleLowerCase()).localeCompare(
    b.code.toLocaleLowerCase()
  )
}

export const formatDateWithHiphen = (day: string | number): string => {
  try {
    const d = String(day)
    return d.slice(0, 4) + '-' + d.slice(4, 6) + '-' + d.slice(6)
  } catch (e) {
    console.error('Invalid date.')
    return ''
  }
}

export const generatedBySelect = (
  showPlansOption: string,
  operationalPlanData: PlanTableRow[],
  userEmail: string,
  setOperationalPlanTableData: (data: PlanTableRow[]) => void
) => {
  if (showPlansOption === PLAN_ACCESS_OPTIONS['Generated by me']) {
    const myPlans: PlanTableRow[] = operationalPlanData.filter(
      (row: PlanTableRow) => row.planGeneratedBy === userEmail
    )
    setOperationalPlanTableData(myPlans)
  } else if (showPlansOption === PLAN_ACCESS_OPTIONS['Not generated by me']) {
    const myPlans: PlanTableRow[] = operationalPlanData.filter(
      (row: PlanTableRow) => row.planGeneratedBy !== userEmail
    )
    setOperationalPlanTableData(myPlans)
  } else if (showPlansOption === PLAN_ACCESS_OPTIONS['Generated by anyone']) {
    setOperationalPlanTableData(operationalPlanData)
  }
}

export const downloadAsCsv = (
  fileName: string,
  dataToDownload: Record<string, AcceptedData>[]
) => {
  const csv = generateCsv(csvConfig)(dataToDownload)

  // Generate the Blob from the CsvOutput
  const blob = asBlob(csvConfig)(csv)

  // Requires URL to be available (web workers or client scripts only)
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${new Date().toISOString()}-${fileName}.csv`
  link.click()
}

// Set of common abbreviations that should remain uppercase
const PRESERVE_UPPERCASE = new Set([
  'SKU',
  'MOQ',
  'ID',
  'API',
  'URL',
  'CSV',
  'PDF',
  'XML',
  'JSON',
  'HTML',
  'CSS',
  'JS',
  'SQL',
  'BOM',
  'ERP',
  'WMS',
  'MRP',
  'SOP',
  'KPI',
])

/**
 * Converts text to sentence case with proper handling of abbreviations
 * @param text - The text to convert
 * @returns Text in sentence case with preserved abbreviations
 */
export const toSentenceCase = (text: string): string => {
  if (!text) return text

  // Split by spaces and process each word
  const words = text.split(' ')
  const result = words.map((word, index) => {
    // Check if word is a preserved abbreviation (case-insensitive check)
    const upperWord = word.toUpperCase()
    if (PRESERVE_UPPERCASE.has(upperWord)) {
      return upperWord
    }

    // First word gets capitalized, others get lowercased unless they're abbreviations
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    } else {
      return word.toLowerCase()
    }
  })

  return result.join(' ')
}

